import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import Keycloak from 'keycloak-js';
import { TracerService } from '../../../services/tracer.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { PersonalToken } from '../../../models/personal-token.model';
import { environment } from '../../../../environments/environment';

// "Personal Tokens" settings tab — long-lived API credentials an admin
// mints for themselves so a script, a CI job or curl can call Tracer
// without driving a browser through Keycloak.
//
// A token carries the owner's permissions, resolved live on every
// request: it can read every session and request its owner can, but it
// can never manage tokens (the backend refuses token-authenticated
// requests on these very endpoints), so revoking a leaked one here
// really does lock the holder out.
//
// The secret is shown exactly ONCE, in `justCreated`, straight from the
// create response. The backend stores only a sha256 of it — there is no
// "show again", and the UI must make that obvious before the user
// navigates away.
@Component({
  selector: 'app-personal-tokens',
  imports: [FormsModule, DatePipe],
  templateUrl: './personal-tokens.html',
})
export class PersonalTokens implements OnInit {
  tokens: PersonalToken[] = [];
  loading = false;
  listError: string | null = null;

  /** Owner shown in the header — the identity every token here acts as. */
  email: string | null = null;

  // Create form
  showCreateForm = false;
  newName = '';
  creating = false;
  createError: string | null = null;

  /** The one-time reveal. Non-null only between a successful create and
   *  the user dismissing it; never populated from a list response,
   *  because the server cannot return a secret it doesn't keep. */
  justCreated: { token: string; details: PersonalToken } | null = null;
  copied = false;

  busyId: number | null = null;

  constructor(
    private tracer: TracerService,
    private ls: LocalStorageService,
    private toastr: ToastrService,
    private router: Router,
    private keycloak: Keycloak
  ) {}

  ngOnInit(): void {
    // Belt-and-braces alongside the route guard: bounce a non-admin
    // before the list request fires a pointless 403.
    if (this.ls.getItem('agRealm') !== 'admins') {
      this.router.navigateByUrl('/');
      return;
    }
    this.email = this.keycloak.tokenParsed?.['email'] ?? null;
    this.refresh();
  }

  /** Base URL to show in the copy-paste curl example. */
  get tracerUrl(): string {
    return environment.tracerUrl;
  }

  /** The API reference this token is the key to. Served by the tracer
   *  backend itself, so it always matches the deployed build and the
   *  environment the user is signed in to. */
  get docsUrl(): string {
    return `${this.tracerUrl}/docs`;
  }

  /** A ready-to-run example using the freshly minted token — the only
   *  moment we can put the real value in it. */
  get curlExample(): string {
    const token = this.justCreated?.token ?? 'YOUR_TOKEN';
    return `curl -H "Authorization: Bearer ${token}" ${this.tracerUrl}/index`;
  }

  refresh(): void {
    this.loading = true;
    this.listError = null;
    this.tracer.listPersonalTokens().subscribe({
      next: res => {
        this.loading = false;
        this.tokens = res.tokens || [];
      },
      error: err => {
        this.loading = false;
        this.listError = err?.error?.error || 'Failed to load your tokens.';
      },
    });
  }

  startCreate(): void {
    this.showCreateForm = true;
    this.newName = '';
    this.createError = null;
  }

  cancelCreate(): void {
    this.showCreateForm = false;
    this.newName = '';
    this.createError = null;
  }

  create(): void {
    const name = this.newName.trim();
    if (!name) {
      this.createError = 'Give the token a name so you can recognise it later.';
      return;
    }

    this.creating = true;
    this.createError = null;
    this.tracer.createPersonalToken(name).subscribe({
      next: res => {
        this.creating = false;
        this.showCreateForm = false;
        this.newName = '';
        this.copied = false;
        this.justCreated = { token: res.token, details: res.token_details };
        this.tokens = [res.token_details, ...this.tokens];
      },
      error: err => {
        this.creating = false;
        this.createError = err?.error?.error || 'Failed to create the token.';
      },
    });
  }

  copyToken(): void {
    if (!this.justCreated) return;
    navigator.clipboard.writeText(this.justCreated.token).then(
      () => {
        this.copied = true;
        this.toastr.success('Token copied to clipboard', 'Copied');
      },
      () =>
        this.toastr.error('Could not copy — select the token and copy it manually', 'Copy failed')
    );
  }

  /** Dismiss the one-time reveal. Deliberately behind a confirm when
   *  the user hasn't copied it yet: closing this panel is the moment
   *  the secret becomes unrecoverable. */
  dismissReveal(): void {
    if (
      !this.copied &&
      !confirm('You have not copied this token yet. It cannot be shown again. Close anyway?')
    ) {
      return;
    }
    this.justCreated = null;
    this.copied = false;
  }

  revoke(token: PersonalToken): void {
    if (
      !confirm(
        `Revoke "${token.name}"? Any script using it stops working immediately. The token stays listed so you can still see when it was last used.`
      )
    ) {
      return;
    }

    this.busyId = token.id;
    this.tracer.revokePersonalToken(token.id).subscribe({
      next: res => {
        this.busyId = null;
        this.tokens = this.tokens.map(t => (t.id === token.id ? res.token_details : t));
        this.toastr.success(`"${token.name}" revoked`, 'Success');
      },
      error: err => {
        this.busyId = null;
        this.toastr.error(err?.error?.error || 'Failed to revoke the token', 'Error');
      },
    });
  }

  remove(token: PersonalToken): void {
    const stillLive = token.status === 'active';
    const warning = stillLive
      ? `Delete "${token.name}"? It is still active — anything using it stops working immediately, and the record that it existed is gone too. This cannot be undone.`
      : `Delete "${token.name}"? The record of this token and its usage history is gone for good.`;
    if (!confirm(warning)) return;

    this.busyId = token.id;
    this.tracer.deletePersonalToken(token.id).subscribe({
      next: () => {
        this.busyId = null;
        this.tokens = this.tokens.filter(t => t.id !== token.id);
        this.toastr.success(`"${token.name}" deleted`, 'Success');
      },
      error: err => {
        this.busyId = null;
        this.toastr.error(err?.error?.error || 'Failed to delete the token', 'Error');
      },
    });
  }

  statusClass(status: PersonalToken['status']): string {
    switch (status) {
      case 'active':
        return 'badge-success';
      case 'revoked':
        return 'badge-error';
      default:
        return 'badge-ghost';
    }
  }
}
